'use client';

import { useFrame, useLoader } from '@react-three/fiber';
import {
  Bloom,
  BrightnessContrast,
  EffectComposer,
  HueSaturation,
  Noise,
  Pixelation,
  Scanline,
  ToneMapping,
  Vignette,
} from '@react-three/postprocessing';
import {
  AerialPerspective,
  Atmosphere,
  Sky,
  SkyLight,
  SunLight,
  Stars,
  type AtmosphereApi,
  type StarsImpl,
} from '@takram/three-atmosphere/r3f';
import type {
  SkyLightProbe,
  StarsMaterial,
  SunDirectionalLight,
} from '@takram/three-atmosphere';
import { Clouds } from '@takram/three-clouds/r3f';
import type { CloudsEffect } from '@takram/three-clouds';
import { Ellipsoid, Geodetic, radians } from '@takram/three-geospatial';
import {
  type BloomEffect,
  type BrightnessContrastEffect,
  type HueSaturationEffect,
  type NoiseEffect,
  type PixelationEffect,
  type ScanlineEffect,
  ToneMappingMode,
  type VignetteEffect,
} from 'postprocessing';
import type { FC, MutableRefObject } from 'react';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import {
  ImageBitmapLoader,
  LinearFilter,
  LinearMipMapLinearFilter,
  MathUtils,
  Matrix4,
  Mesh,
  NoColorSpace,
  PlaneGeometry,
  RepeatWrapping,
  Texture,
  Vector3,
} from 'three';
import { FisheyeEffect } from './fisheyeEffect';
import { createWaterMaterial } from './waterMaterial';
import { useSkyEnvironment } from './useSkyEnvironment';
import { FlightTraffic } from './FlightTraffic';
import { SkyCamera, type CameraFrame } from './SkyCamera';
import { SunLensFlare } from './SunLensFlare';
import { POST_DEFAULTS, skyState, subscribeSky, updateSky } from '../skyStore';
import { CloudInteractionDirector } from './CloudInteractionDirector';

const LAT = 50.08; // Prague
const LNG = 14.44;

const dateAtHour = (hour: number): Date => {
  const now = new Date();
  return new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    Math.floor(hour),
    Math.round((hour % 1) * 60)
  );
};

// Turbopack loads the worker through a blob: URL, which cannot serve as a
// base for root-relative fetches — resolve asset paths against the origin
// explicitly (blob: URLs still report their creator's origin).
const assetUrl = (path: string): string =>
  typeof location !== 'undefined' && location.origin !== 'null'
    ? new URL(path, location.origin).href
    : path;

// react-postprocessing's wrapEffect memoizes on JSON.stringify(props). With
// React 19, `ref` is an ordinary prop, and a ref OBJECT holding a mounted
// effect (which references internal scenes/cameras) makes that stringify
// throw on circular structures. Callback refs dodge it: JSON.stringify
// silently drops function values.
const captureRef =
  <T,>(target: MutableRefObject<T | null>) =>
  (value: T | null) => {
    target.current = value;
  };

// ImageBitmapLoader works on both threads; flip at decode so the result
// matches what TextureLoader would have produced.
const flipY = (loader: ImageBitmapLoader) => {
  loader.setOptions({ imageOrientation: 'flipY' });
};

const useBitmapTexture = (url: string): Texture => {
  const bitmap = useLoader(ImageBitmapLoader, assetUrl(url), flipY);
  const texture = useMemo(() => {
    const texture = new Texture(bitmap as unknown as HTMLImageElement);
    texture.flipY = false;
    texture.needsUpdate = true;
    return texture;
  }, [bitmap]);
  useEffect(() => () => texture.dispose(), [texture]);
  return texture;
};

// The sun the sky is actually lit by is clamped into a dusk/dawn band, but the
// stars must still come out on a real night — so their visibility is judged by
// the true sun, kept here.

const CelestialClock: FC<{
  atmosphereRef: MutableRefObject<AtmosphereApi | null>;
}> = ({ atmosphereRef }) => {
  const frame = useMemo(() => {
    const position = new Geodetic(radians(LNG), radians(LAT), 0).toECEF();
    return Ellipsoid.WGS84.getNorthUpEastFrame(position, new Matrix4());
  }, []);

  useFrame(() => {
    const api = atmosphereRef.current;
    if (!api) return;
    api.worldToECEFMatrix.copy(frame);
    const now = new Date();
    const baseHour =
      skyState.hour ??
      now.getHours() + now.getMinutes() / 60 + now.getSeconds() / 3600;

    api.updateByDate(skyState.hour === null ? now : dateAtHour(baseHour));
  });

  return null;
};

const CatalogStars: FC<{
  atmosphereRef: MutableRefObject<AtmosphereApi | null>;
}> = ({ atmosphereRef }) => {
  const starsRef = useRef<StarsImpl>(null);
  const scratch = useMemo(() => new Matrix4(), []);
  const sunWorld = useMemo(() => new Vector3(), []);

  useFrame(() => {
    const api = atmosphereRef.current;
    const stars = starsRef.current;
    if (!api || !stars) return;
    const rot = scratch.extractRotation(api.worldToECEFMatrix).transpose();
    sunWorld.copy(api.sunDirection).applyMatrix4(rot);
    const night = skyState.stars.forceNight
      ? 1
      : MathUtils.smoothstep(-sunWorld.y, 0.0, 0.09);
    (stars.material as StarsMaterial).intensity =
      skyState.stars.intensity * night;
  });

  return (
    <Stars
      ref={starsRef}
      data={assetUrl('/takram/stars.bin')}
      intensity={0}
      pointSize={2}
    />
  );
};

const CloudsDirector: FC<{
  effectRef: MutableRefObject<CloudsEffect | null>;
}> = ({ effectRef }) => {
  const clearProgress = useRef(0);
  const elapsed = useRef(0);

  useFrame((_, delta) => {
    elapsed.current += delta;
    const { cloudsDelay, cloudsDuration } = skyState.intro;
    const intro = MathUtils.smoothstep(
      elapsed.current,
      cloudsDelay,
      cloudsDelay + cloudsDuration
    );
    const target = skyState.cloudsClear ? 1 : 0;
    clearProgress.current +=
      (target - clearProgress.current) * Math.min(1, delta * 1.2);
    const effect = effectRef.current;
    if (effect) {
      effect.coverage = skyState.coverage * (1 - clearProgress.current) * intro;
      const lighting = skyState.cloudLighting;
      effect.minIrradiance = lighting.minIrradiance * intro;
      effect.skyLightScale = lighting.skyLightScale;
      effect.powderScale = lighting.powderScale;
      effect.powderExponent = lighting.powderExponent;
    }
  });
  return null;
};

const PixelationDirector: FC<{
  effectRef: MutableRefObject<PixelationEffect | null>;
}> = ({ effectRef }) => {
  const granularity = useRef(80);
  const time = useRef(0);

  useFrame((_, delta) => {
    time.current += delta;
    granularity.current = MathUtils.lerp(
      granularity.current,
      2 + Math.sin(time.current) * 5,
      0.005
    );
    if (effectRef.current) {
      effectRef.current.granularity = skyState.post.pixelation
        ? granularity.current
        : 0;
    }
  });
  return null;
};

interface GradeRefs {
  noise: MutableRefObject<NoiseEffect | null>;
  bloom: MutableRefObject<BloomEffect | null>;
  scanline: MutableRefObject<ScanlineEffect | null>;
  hueSaturation: MutableRefObject<HueSaturationEffect | null>;
  brightnessContrast: MutableRefObject<BrightnessContrastEffect | null>;
  vignette: MutableRefObject<VignetteEffect | null>;
  fisheye: MutableRefObject<FisheyeEffect | null>;
}

const GradeDirector: FC<{ refs: GradeRefs }> = ({ refs }) => {
  useFrame(() => {
    const post = skyState.post;
    const noise = refs.noise.current;
    if (noise) noise.blendMode.opacity.value = post.noiseOpacity;
    const bloom = refs.bloom.current;
    if (bloom) {
      bloom.intensity = post.bloomIntensity;
      bloom.luminanceMaterial.threshold = post.bloomThreshold;
      bloom.luminanceMaterial.smoothing = post.bloomSmoothing;
    }
    const scanline = refs.scanline.current;
    if (scanline) {
      scanline.density = post.scanlineDensity;
      scanline.blendMode.opacity.value = post.scanlineOpacity;
    }
    const hueSaturation = refs.hueSaturation.current;
    if (hueSaturation) hueSaturation.saturation = post.saturation;
    const brightnessContrast = refs.brightnessContrast.current;
    if (brightnessContrast) {
      brightnessContrast.brightness =
        post.brightness - skyState.camera.darkenOnScroll * skyState.darken;
      brightnessContrast.contrast = post.contrast;
    }
    const vignette = refs.vignette.current;
    if (vignette) {
      vignette.offset = post.vignetteOffset;
      vignette.darkness = post.vignetteDarkness;
    }
    const fisheye = refs.fisheye.current;
    if (fisheye) {
      fisheye.strength = post.fisheyeStrength;
      fisheye.zoom = post.fisheyeZoom;
    }
  });
  return null;
};

const ReadyBeacon: FC<{ onReady: () => void }> = ({ onReady }) => {
  const fired = useRef(false);
  const smooth = useRef(0);

  useFrame((_, delta) => {
    if (fired.current) return;
    // The first frames land inside the boot stall, and the page's fade is a
    // compositor animation — starting it there means it can't render and the
    // sky appears to pop. Wait for a run of genuinely cheap frames, so the
    // GPU is free by the time the fade plays.
    smooth.current = delta < 0.04 ? smooth.current + 1 : 0;
    if (smooth.current < 6) return;
    fired.current = true;
    onReady();
  });
  return null;
};

const FpsMeter: FC<{ onFps: (fps: number) => void }> = ({ onFps }) => {
  const frames = useRef(0);
  const last = useRef(0);

  useFrame(({ clock }) => {
    frames.current += 1;
    const t = clock.elapsedTime;
    if (t - last.current >= 0.5) {
      onFps(frames.current / (t - last.current));
      frames.current = 0;
      last.current = t;
    }
  });
  return null;
};

// Degrading renders the clouds at a lower internal resolution rather than
// switching takram's `low` preset, which turns off accurate sun/sky light and
// leaves the deck unlit.
const QUALITY_FLOOR_FPS = 40;
const QUALITY_BAD_SECONDS = 4;
const QUALITY_WARMUP_SECONDS = 6;
const LOW_RESOLUTION_SCALE = 0.5;

const AdaptiveQuality: FC = () => {
  const frames = useRef(0);
  const bucketStart = useRef(0);
  const badSeconds = useRef(0);
  const armAt = useRef(0);
  const done = useRef(false);

  useFrame(({ clock }) => {
    if (done.current) return;
    const t = clock.elapsedTime;
    // Ignore the intro: pixelation, cloud build and shader compilation all
    // stutter before the scene settles, and that isn't the steady-state cost.
    if (armAt.current === 0) armAt.current = t + QUALITY_WARMUP_SECONDS;
    if (t < armAt.current) {
      bucketStart.current = t;
      frames.current = 0;
      return;
    }

    frames.current += 1;
    const span = t - bucketStart.current;
    if (span < 1) return;

    const fps = frames.current / span;
    badSeconds.current = fps < QUALITY_FLOOR_FPS ? badSeconds.current + 1 : 0;
    frames.current = 0;
    bucketStart.current = t;

    if (badSeconds.current >= QUALITY_BAD_SECONDS) {
      done.current = true;
      if (skyState.cloudsQuality !== 'low') {
        updateSky({ cloudsQuality: 'low' });
      }
    }
  });
  return null;
};

const SeaWater: FC<{
  atmosphereRef: MutableRefObject<AtmosphereApi | null>;
  sunRef: MutableRefObject<SunDirectionalLight | null>;
  skyLightRef: MutableRefObject<SkyLightProbe | null>;
}> = ({ atmosphereRef, sunRef, skyLightRef }) => {
  const skyMap = useSkyEnvironment(atmosphereRef);
  const normals = useBitmapTexture('/sky/waternormals.jpg');
  const up = useMemo(() => new Vector3(0, 1, 0), []);
  normals.wrapS = RepeatWrapping;
  normals.wrapT = RepeatWrapping;

  const water = useMemo(() => {
    const geometry = new PlaneGeometry(1_000_000, 1_000_000);
    const surface = new Mesh(geometry, createWaterMaterial(normals, skyMap));
    surface.rotation.x = -Math.PI / 2;
    surface.position.set(0, 0, 0);
    return surface;
  }, [normals, skyMap]);

  useEffect(
    () => () => {
      water.geometry.dispose();
      water.material.dispose();
    },
    [water]
  );

  useFrame((_, delta) => {
    const settings = skyState.water;
    const { uniforms } = water.material;
    uniforms.time.value += delta * settings.speed;
    uniforms.size.value = 1 / settings.rippleSize;
    uniforms.distortionScale.value = settings.distortion;
    uniforms.roughness.value = settings.roughness;
    uniforms.minimumLight.value = skyState.cloudLighting.minIrradiance;
    uniforms.waterColor.value.set(settings.color);
    const sun = sunRef.current;
    const skyLight = skyLightRef.current;
    if (sun) {
      uniforms.sunDirection.value
        .copy(sun.position)
        .sub(sun.target.position)
        .normalize();
      uniforms.sunColor.value
        .copy(sun.color)
        .multiplyScalar(sun.intensity * settings.sunlight);
    }
    if (skyLight) {
      skyLight.sh.getIrradianceAt(up, uniforms.skyIrradiance.value);
      uniforms.skyIrradiance.value.multiplyScalar(skyLight.intensity);
    } else {
      uniforms.skyIrradiance.value.setScalar(0);
    }
    uniforms.skyIrradiance.value.clampScalar(
      skyState.cloudLighting.minIrradiance,
      Infinity
    );
  });

  return <primitive object={water} />;
};

const CloudsWithBitmapTextures: FC<{
  effectRef: MutableRefObject<CloudsEffect | null>;
  quality: 'low' | 'medium';
}> = ({ effectRef, quality }) => {
  // The takram loader for these PNGs is DOM-based; hand it ready textures with
  // the same filter/wrap settings it would have applied itself.
  const localWeather = useBitmapTexture('/takram/local_weather.png');
  const turbulence = useBitmapTexture('/takram/turbulence.png');
  for (const texture of [localWeather, turbulence]) {
    texture.minFilter = LinearMipMapLinearFilter;
    texture.magFilter = LinearFilter;
    texture.wrapS = RepeatWrapping;
    texture.wrapT = RepeatWrapping;
    texture.colorSpace = NoColorSpace;
  }

  return (
    <Clouds
      ref={captureRef(effectRef)}
      qualityPreset="medium"
      resolutionScale={quality === 'low' ? LOW_RESOLUTION_SCALE : 1}
      coverage={0.5}
      localWeatherTexture={localWeather}
      shapeTexture={assetUrl('/takram/shape.bin')}
      shapeDetailTexture={assetUrl('/takram/shape_detail.bin')}
      turbulenceTexture={turbulence}
      stbnTexture={assetUrl('/takram/stbn.bin')}
      localWeatherVelocity={[0.0002, 0]}
      shapeVelocity={[0.0005, 0, 0.0001]}
      shapeDetailVelocity={[0.001, 0, 0.0002]}
    />
  );
};

export const SkySceneContent: FC<{
  onFps?: (fps: number) => void;
  onReady?: () => void;
  onCamera?: (camera: CameraFrame) => void;
}> = ({ onFps, onReady, onCamera }) => {
  const atmosphereRef = useRef<AtmosphereApi | null>(null);
  const sunRef = useRef<SunDirectionalLight | null>(null);
  const skyLightRef = useRef<SkyLightProbe | null>(null);
  const pixelationRef = useRef<PixelationEffect | null>(null);
  const noiseRef = useRef<NoiseEffect | null>(null);
  const bloomRef = useRef<BloomEffect | null>(null);
  const scanlineRef = useRef<ScanlineEffect | null>(null);
  const hueSaturationRef = useRef<HueSaturationEffect | null>(null);
  const brightnessContrastRef = useRef<BrightnessContrastEffect | null>(null);
  const vignetteRef = useRef<VignetteEffect | null>(null);
  const fisheye = useMemo(
    () =>
      new FisheyeEffect({
        strength: POST_DEFAULTS.fisheyeStrength,
        zoom: POST_DEFAULTS.fisheyeZoom,
      }),
    []
  );
  useEffect(() => () => fisheye.dispose(), [fisheye]);
  const fisheyeRef = useRef<FisheyeEffect | null>(fisheye);
  const gradeRefs: GradeRefs = {
    noise: noiseRef,
    bloom: bloomRef,
    scanline: scanlineRef,
    hueSaturation: hueSaturationRef,
    brightnessContrast: brightnessContrastRef,
    vignette: vignetteRef,
    fisheye: fisheyeRef,
  };
  const cloudsRef = useRef<CloudsEffect | null>(null);
  // Individual primitive states so unchanged store notifications bail out of
  // re-rendering (scroll updates arrive every frame during scrolling).
  const [cloudsEnabled, setCloudsEnabled] = useState(skyState.cloudsEnabled);
  const [postEnabled, setPostEnabled] = useState(skyState.postEnabled);
  const [cloudsQuality, setCloudsQuality] = useState(skyState.cloudsQuality);
  const [sceneMode, setSceneMode] = useState(skyState.sceneMode);

  useEffect(
    () =>
      subscribeSky(() => {
        setCloudsEnabled(skyState.cloudsEnabled);
        setPostEnabled(skyState.postEnabled);
        setCloudsQuality(skyState.cloudsQuality);
        setSceneMode(skyState.sceneMode);
      }),
    []
  );

  return (
    <Atmosphere ref={atmosphereRef}>
      <CelestialClock atmosphereRef={atmosphereRef} />
      <SkyCamera onCamera={onCamera} />
      {onFps != null && <FpsMeter onFps={onFps} />}
      <AdaptiveQuality />
      {onReady != null && <ReadyBeacon onReady={onReady} />}
      <PixelationDirector effectRef={pixelationRef} />
      <GradeDirector refs={gradeRefs} />
      <CloudsDirector effectRef={cloudsRef} />
      <CloudInteractionDirector effectRef={cloudsRef} />
      <Suspense fallback={null}>
        <Sky />
        <SunLight ref={sunRef} />
        <SkyLight ref={skyLightRef} />
        <CatalogStars atmosphereRef={atmosphereRef} />
        <SeaWater
          atmosphereRef={atmosphereRef}
          sunRef={sunRef}
          skyLightRef={skyLightRef}
        />
        {sceneMode === 'sky' && <FlightTraffic />}
        <EffectComposer multisampling={0}>
          <>
            {cloudsEnabled && (
              <CloudsWithBitmapTextures
                effectRef={cloudsRef}
                quality={cloudsQuality}
              />
            )}
          </>
          <AerialPerspective />
          <SunLensFlare atmosphereRef={atmosphereRef} cloudsRef={cloudsRef} />
          <ToneMapping mode={ToneMappingMode.AGX} />
          <primitive object={fisheye} />
          <>
            {postEnabled && (
              <>
                <Pixelation ref={captureRef(pixelationRef)} granularity={80} />
                <Noise
                  ref={captureRef(noiseRef)}
                  opacity={POST_DEFAULTS.noiseOpacity}
                />
                <Bloom
                  ref={captureRef(bloomRef)}
                  intensity={POST_DEFAULTS.bloomIntensity}
                  luminanceThreshold={POST_DEFAULTS.bloomThreshold}
                  luminanceSmoothing={POST_DEFAULTS.bloomSmoothing}
                  mipmapBlur={true}
                />
                <Scanline
                  ref={captureRef(scanlineRef)}
                  density={POST_DEFAULTS.scanlineDensity}
                  opacity={POST_DEFAULTS.scanlineOpacity}
                />
                <HueSaturation
                  ref={captureRef(hueSaturationRef)}
                  saturation={POST_DEFAULTS.saturation}
                />
                <BrightnessContrast
                  ref={captureRef(brightnessContrastRef)}
                  brightness={POST_DEFAULTS.brightness}
                  contrast={POST_DEFAULTS.contrast}
                />
                <Vignette
                  ref={captureRef(vignetteRef)}
                  offset={POST_DEFAULTS.vignetteOffset}
                  darkness={POST_DEFAULTS.vignetteDarkness}
                  eskil={false}
                />
              </>
            )}
          </>
        </EffectComposer>
      </Suspense>
    </Atmosphere>
  );
};
